/** Scoring sheet cache types. Event types (all-event, high game, high series) stay on Standings. */
export const SCORING_CACHE_TYPES = {
  teamScratch: "team_scratch",
  teamHandicap: "team_handicap",
  scratch: "scratch",
  handicap: "handicap",
};

export const scoringCacheKey = (teamId, bowlerId, calculationType) =>
  `${teamId}-${bowlerId}-${calculationType}`;

export const cacheRowIsCurrent = (row, resultsVersion) => {
  if (!row) return false;
  return Number(row.results_version) === Number(resultsVersion);
};

export const mapCalculationCacheToHub = (rows = []) => {
  const calculationResults = {};
  const byTeam = new Map();

  rows.forEach((row) => {
    if (!row?.team_id || !row.calculation_type) return;
    if (!byTeam.has(row.team_id)) {
      byTeam.set(row.team_id, {
        scratchTotal: 0,
        handicapTotal: 0,
        bowlers: {},
      });
    }
    const team = byTeam.get(row.team_id);

    if (row.calculation_type === SCORING_CACHE_TYPES.teamScratch) {
      team.scratchTotal = Number(row.scratch_total) || 0;
      calculationResults[scoringCacheKey(row.team_id, "team", "scratch")] = {
        teamScratchTotal: team.scratchTotal,
      };
      return;
    }

    if (row.calculation_type === SCORING_CACHE_TYPES.teamHandicap) {
      team.handicapTotal = Number(row.handicap_total) || 0;
      calculationResults[scoringCacheKey(row.team_id, "team", "handicap")] = {
        teamHandicapTotal: team.handicapTotal,
      };
      return;
    }

    if (!row.bowler_id) return;
    if (!team.bowlers[row.bowler_id]) {
      team.bowlers[row.bowler_id] = {
        scratchTotal: 0,
        handicap: 0,
        handicapTotal: 0,
      };
    }
    const bowler = team.bowlers[row.bowler_id];

    if (row.calculation_type === SCORING_CACHE_TYPES.scratch) {
      bowler.scratchTotal = Number(row.scratch_total) || 0;
      calculationResults[
        scoringCacheKey(row.team_id, row.bowler_id, "scratch")
      ] = {
        total: bowler.scratchTotal,
        scratchTotal: bowler.scratchTotal,
      };
      return;
    }

    if (row.calculation_type === SCORING_CACHE_TYPES.handicap) {
      bowler.handicap = Number(row.handicap_value) || 0;
      bowler.handicapTotal = Number(row.handicap_total) || 0;
      calculationResults[
        scoringCacheKey(row.team_id, row.bowler_id, "handicap")
      ] = {
        handicapTotal: bowler.handicapTotal,
        handicap: bowler.handicap,
      };
    }
  });

  return { calculationResults, byTeam };
};

export const teamCacheIsReady = (rows, team, resultsVersion) => {
  if (!team?.id || !team.eventsID) return true;
  const current = (rows || []).filter(
    (row) =>
      row.team_id === team.id && cacheRowIsCurrent(row, resultsVersion)
  );
  const hasTeamScratch = current.some(
    (row) =>
      row.calculation_type === SCORING_CACHE_TYPES.teamScratch && !row.bowler_id
  );
  const hasTeamHandicap = current.some(
    (row) =>
      row.calculation_type === SCORING_CACHE_TYPES.teamHandicap && !row.bowler_id
  );
  if (!hasTeamScratch || !hasTeamHandicap) return false;

  const bowlerIds = (team.bowlers || []).map((bowler) => bowler.id).filter(Boolean);
  return bowlerIds.every((bowlerId) => {
    const hasScratch = current.some(
      (row) =>
        row.calculation_type === SCORING_CACHE_TYPES.scratch &&
        row.bowler_id === bowlerId
    );
    const hasHandicap = current.some(
      (row) =>
        row.calculation_type === SCORING_CACHE_TYPES.handicap &&
        row.bowler_id === bowlerId
    );
    return hasScratch && hasHandicap;
  });
};

export const cacheRowsFromCalculateScoresResults = (
  results = [],
  resultsVersion = 0
) => {
  const rows = [];
  (results || []).forEach((item) => {
    if (!item?.success || !item.result || !item.teamId) return;
    const result = item.result;
    const teamId = item.teamId;

    if (result.type === SCORING_CACHE_TYPES.teamScratch) {
      rows.push({
        team_id: teamId,
        bowler_id: null,
        calculation_type: SCORING_CACHE_TYPES.teamScratch,
        scratch_total: Number(result.teamScratchTotal) || 0,
        handicap_total: 0,
        handicap_value: 0,
        results_version: resultsVersion,
      });
      return;
    }

    if (result.type !== SCORING_CACHE_TYPES.teamHandicap) return;

    rows.push({
      team_id: teamId,
      bowler_id: null,
      calculation_type: SCORING_CACHE_TYPES.teamHandicap,
      scratch_total: Number(result.teamScratchTotal) || 0,
      handicap_total: Number(result.teamHandicapTotal) || 0,
      handicap_value: 0,
      results_version: resultsVersion,
    });

    (result.bowlerTotals || []).forEach((bowlerTotal) => {
      if (!bowlerTotal?.bowlerId) return;
      rows.push({
        team_id: teamId,
        bowler_id: bowlerTotal.bowlerId,
        calculation_type: SCORING_CACHE_TYPES.scratch,
        scratch_total: Number(bowlerTotal.scratchTotal) || 0,
        handicap_total: 0,
        handicap_value: 0,
        results_version: resultsVersion,
      });
      rows.push({
        team_id: teamId,
        bowler_id: bowlerTotal.bowlerId,
        calculation_type: SCORING_CACHE_TYPES.handicap,
        scratch_total: Number(bowlerTotal.scratchTotal) || 0,
        handicap_total: Number(bowlerTotal.handicapTotal) || 0,
        handicap_value: Number(bowlerTotal.handicap) || 0,
        results_version: resultsVersion,
      });
    });
  });
  return rows;
};

export const scoresObjectForTeam = (team) => {
  const gamesPerEvent = team.gamesPerEvent || 3;
  const scores = {};
  (team.bowlers || []).forEach((bowler) => {
    const games = [];
    for (let i = 0; i < gamesPerEvent; i += 1) {
      const existing = (team.scores || []).find(
        (score) => score.bowlerID === bowler.id && score.gameNumber === i + 1
      );
      games[i] =
        existing?.score === null || existing?.score === undefined
          ? 0
          : existing.score;
    }
    scores[bowler.id] = games;
  });
  return scores;
};
